import { useEffect, useMemo, useState } from "react";
import { Button, SettingsPanel } from "@/shared";
import { SettingsHeader } from ".";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/access-control";
import { toast } from "react-toastify";
import CodeEditor from "@/shared/CodeEditor";
import { useConfig } from "@/hooks";
import {
  useGetCustomerConfigRawQuery,
  useUpdateCustomerConfigRawMutation,
} from "@/redux/services/config/configApi";

const tryParseJson = (value) => {
  try {
    return { ok: true, value: JSON.parse(value) };
  } catch (err) {
    return { ok: false, error: err };
  }
};

const toPrettyJson = (obj) => JSON.stringify(obj, null, 2);

const AdvancedSettings = () => {
  const { hasPermission } = usePermissions();
  const canManageAdvanced = hasPermission(PERMISSIONS.SETTINGS_ADVANCED_MANAGE);
  const { parentCompany } = useConfig();
  const [draft, setDraft] = useState("");

  const {
    data: rawData,
    isLoading: rawLoading,
    isFetching: rawFetching,
    isError: rawError,
    refetch,
  } = useGetCustomerConfigRawQuery(
    { parentCompany },
    { skip: !parentCompany }
  );

  const [updateCustomerConfigRaw, { isLoading: saving }] =
    useUpdateCustomerConfigRawMutation();

  const serverJsonString = useMemo(() => {
    if (!rawData?.customerConfig) return "";
    return toPrettyJson(rawData.customerConfig);
  }, [rawData?.customerConfig]);

  useEffect(() => {
    // Initialize editor from server data once.
    if (serverJsonString && !draft) {
      setDraft(serverJsonString);
    }
  }, [serverJsonString]); // intentionally not depending on draft

  const parsed = useMemo(() => tryParseJson(draft || ""), [draft]);
  const hasValidJson = draft.length > 0 && parsed.ok;

  const actions = (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="secondary"
        disabled={!parentCompany || rawLoading || rawFetching}
        onClick={() => {
          setDraft(serverJsonString || "");
          refetch();
        }}
      >
        Reload
      </Button>
      <Button
        type="button"
        variant="secondary"
        disabled={!hasValidJson}
        onClick={() => {
          const next = toPrettyJson(parsed.value);
          setDraft(next);
        }}
      >
        Format
      </Button>
      <Button
        type="button"
        disabled={!parentCompany || !hasValidJson || saving}
        loading={saving}
        onClick={async () => {
          if (!parentCompany) return;
          const res = tryParseJson(draft);
          if (!res.ok) {
            toast.error("Invalid JSON. Please fix errors before saving.");
            return;
          }
          try {
            await updateCustomerConfigRaw({
              parentCompany,
              customerConfig: res.value,
            }).unwrap();
            toast.success("Customer config saved");
          } catch (err) {
            console.error("Failed to save customer config:", err);
            toast.error("Failed to save customer config");
          }
        }}
      >
        Save
      </Button>
    </div>
  );

  const data = [
    {
      // No title/subtitle here so SettingsPanel renders a single full-width section
      content: (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">
              Customer Config (JSON)
            </div>
            <div className="text-xs text-gray-500">
              Advanced: edits the tenant’s customer-config JSON. Changes apply
              across web + mobile after refresh.
            </div>
          </div>

          <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
            <div className="font-semibold">Be careful</div>
            <div>
              Invalid or risky values can break workflows. Prefer small, reviewed
              edits and keep JSON valid.
            </div>
          </div>

          {!parentCompany ? (
            <div className="text-sm text-gray-600">
              No parent company found for this session.
            </div>
          ) : rawError ? (
            <div className="text-sm text-red-600">
              Failed to load customer config.
            </div>
          ) : null}

          <CodeEditor
            value={draft}
            onChange={(value) => setDraft(value)}
            language="json"
            height="calc(100vh - 360px)"
            theme="dark"
            placeholder='{\n  "features": { ... }\n}'
            readOnly={!parentCompany || rawLoading}
          />

          {!draft ? null : parsed.ok ? (
            <div className="text-xs text-green-700">JSON is valid.</div>
          ) : (
            <div className="text-xs text-red-600">
              JSON error: {String(parsed.error?.message || "Invalid JSON")}
            </div>
          )}
        </div>
      ),
    },
  ];

  if (!canManageAdvanced) {
    return (
      <div className="bg-white border rounded-lg p-4">
        <div className="font-semibold text-gray-900">Access restricted</div>
        <div className="text-sm text-gray-600">
          You don’t have permission to view advanced settings.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SettingsHeader title="Advanced" actions={actions} />
      <SettingsPanel title="Advanced Settings" subtitle="" data={data} />
    </div>
  );
};

export default AdvancedSettings;


